import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { ShoppingCart, Eye, CheckCircle, XCircle, Clock, Truck, Package, Filter, Search, MapPin, Phone, Mail, CreditCard, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import './Pedidos.css';

const Pedidos = () => {
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
  const [modalDetalhes, setModalDetalhes] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    'pedidos',
    async () => {
      const response = await api.get('/api/pedidos');
      return response.data.data.pedidos;
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, status }) => api.patch(`/api/pedidos/${id}/status`, { status }),
    {
      onSuccess: () => {
        toast.success('Status atualizado!');
        queryClient.invalidateQueries('pedidos');
        setModalDetalhes(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar status');
      }
    }
  );

  const updatePagamentoMutation = useMutation(
    ({ id, statusPagamento }) => api.patch(`/api/pedidos/${id}/pagamento`, { statusPagamento }),
    {
      onSuccess: () => {
        toast.success('Status de pagamento atualizado!');
        queryClient.invalidateQueries('pedidos');
        setModalDetalhes(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar pagamento');
      }
    }
  );

  if (isLoading) {
    return <div className="loading">Carregando...</div>;
  }

  const getStatusColor = (status) => {
    const colors = {
      'Pendente': 'orange',
      'Confirmado': 'blue',
      'Em preparo': 'purple',
      'Pronto': 'green',
      'Saiu para entrega': 'blue',
      'Entregue': 'success',
      'Cancelado': 'danger'
    };
    return colors[status] || 'gray';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pendente': Clock,
      'Confirmado': CheckCircle,
      'Em preparo': Package,
      'Pronto': CheckCircle,
      'Saiu para entrega': Truck,
      'Entregue': CheckCircle,
      'Cancelado': XCircle
    };
    return icons[status] || Clock;
  };

  const getProximoStatus = (status) => {
    const fluxo = {
      'Pendente': 'Confirmado',
      'Confirmado': 'Em preparo',
      'Em preparo': 'Pronto',
      'Pronto': 'Saiu para entrega',
      'Saiu para entrega': 'Entregue'
    };
    return fluxo[status];
  };

  const pedidosFiltrados = data?.filter(pedido => {
    const matchStatus = filtroStatus === 'todos' || pedido.status === filtroStatus;
    const matchBusca = !busca || 
      pedido.numero?.toLowerCase().includes(busca.toLowerCase()) ||
      pedido.cliente?.nome?.toLowerCase().includes(busca.toLowerCase()) ||
      pedido.cliente?.telefone?.includes(busca);
    return matchStatus && matchBusca;
  }) || [];

  const handleVerDetalhes = async (pedidoId) => {
    try {
      const response = await api.get(`/api/pedidos/${pedidoId}`);
      setPedidoSelecionado(response.data.data.pedido);
      setModalDetalhes(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do pedido');
    }
  };

  const handleAtualizarStatus = (pedidoId, novoStatus) => {
    if (window.confirm(`Deseja realmente atualizar o status para "${novoStatus}"?`)) {
      updateStatusMutation.mutate({ id: pedidoId, status: novoStatus });
    }
  };

  const handleAtualizarPagamento = (pedidoId, novoStatus) => {
    if (window.confirm(`Deseja realmente atualizar o status de pagamento para "${novoStatus}"?`)) {
      updatePagamentoMutation.mutate({ id: pedidoId, statusPagamento: novoStatus });
    }
  };

  const totalPedidos = pedidosFiltrados.length;
  const totalPendentes = pedidosFiltrados.filter(p => p.status === 'Pendente').length;
  const totalEmPreparo = pedidosFiltrados.filter(p => p.status === 'Em preparo').length;
  const totalHoje = pedidosFiltrados.filter(p => {
    const hoje = new Date();
    const dataPedido = new Date(p.createdAt);
    return dataPedido.toDateString() === hoje.toDateString();
  }).length;

  return (
    <div className="pedidos-page">
      <div className="page-header">
        <div>
          <h2><ShoppingCart size={28} /> Pedidos</h2>
          <p>Gerencie todos os pedidos do sistema</p>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="pedidos-stats">
        <div className="stat-card">
          <div className="stat-icon blue">
            <ShoppingCart size={24} />
          </div>
          <div className="stat-info">
            <h3>{totalPedidos}</h3>
            <p>Total de Pedidos</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <Clock size={24} />
          </div>
          <div className="stat-info">
            <h3>{totalPendentes}</h3>
            <p>Pendentes</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <Package size={24} />
          </div>
          <div className="stat-info">
            <h3>{totalEmPreparo}</h3>
            <p>Em Preparo</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <CheckCircle size={24} />
          </div>
          <div className="stat-info">
            <h3>{totalHoje}</h3>
            <p>Hoje</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="pedidos-filters">
        <div className="filter-group">
          <Filter size={20} />
          <select 
            value={filtroStatus} 
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="filter-select"
          >
            <option value="todos">Todos os Status</option>
            <option value="Pendente">Pendente</option>
            <option value="Confirmado">Confirmado</option>
            <option value="Em preparo">Em preparo</option>
            <option value="Pronto">Pronto</option>
            <option value="Saiu para entrega">Saiu para entrega</option>
            <option value="Entregue">Entregue</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>
        <div className="filter-group">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por número, cliente ou telefone..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="filter-input"
          />
        </div>
      </div>

      {/* Tabela de Pedidos */}
      <div className="card">
        <table className="pedidos-table">
          <thead>
            <tr>
              <th>Número</th>
              <th>Cliente</th>
              <th>Total</th>
              <th>Status</th>
              <th>Pagamento</th>
              <th>Data</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {pedidosFiltrados.map((pedido) => {
              const StatusIcon = getStatusIcon(pedido.status);
              const proximoStatus = getProximoStatus(pedido.status);
              
              return (
                <tr key={pedido._id}>
                  <td>
                    <strong>#{pedido.numero}</strong>
                  </td>
                  <td>
                    <div className="cliente-info">
                      <strong>{pedido.cliente?.nome || 'N/A'}</strong>
                      {pedido.cliente?.telefone && (
                        <span className="cliente-telefone">
                          <Phone size={14} /> {pedido.cliente.telefone}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <strong style={{ fontSize: '1rem' }}>R$ {pedido.total?.toFixed(2)}</strong>
                      {pedido.tipoEntrega === 'delivery' && pedido.taxaEntrega > 0 && (
                        <span style={{ fontSize: '0.75rem', color: '#059669', marginTop: '2px' }}>
                          (Taxa: R$ {pedido.taxaEntrega.toFixed(2)})
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-${getStatusColor(pedido.status)}`}>
                      <StatusIcon size={16} />
                      {pedido.status}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${pedido.statusPagamento === 'Pago' ? 'success' : 'orange'}`}>
                      {pedido.statusPagamento || 'Pendente'}
                    </span>
                  </td>
                  <td>
                    {format(new Date(pedido.createdAt), 'dd/MM/yyyy HH:mm')}
                  </td>
                  <td>
                    <div className="acoes-pedido">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleVerDetalhes(pedido._id)}
                        title="Ver detalhes"
                      >
                        <Eye size={16} />
                      </button>
                      {proximoStatus && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAtualizarStatus(pedido._id, proximoStatus)}
                          title={`Atualizar para ${proximoStatus}`}
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {pedidosFiltrados.length === 0 && (
          <div className="empty-state">
            <ShoppingCart size={64} />
            <p>Nenhum pedido encontrado</p>
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {modalDetalhes && pedidoSelecionado && (
        <div className="modal-overlay" onClick={() => setModalDetalhes(false)}>
          <div className="modal-content modal-pedido" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Pedido #{pedidoSelecionado.numero}</h3>
              <button className="modal-close" onClick={() => setModalDetalhes(false)}>
                <XCircle size={24} />
              </button>
            </div>

            <div className="modal-body">
              {/* Informações do Cliente */}
              <div className="pedido-section">
                <h4>Cliente</h4>
                <div className="pedido-info-grid">
                  <div>
                    <strong>Nome:</strong> {pedidoSelecionado.cliente?.nome || 'N/A'}
                  </div>
                  <div>
                    <Phone size={16} /> {pedidoSelecionado.cliente?.telefone || 'N/A'}
                  </div>
                  {pedidoSelecionado.cliente?.whatsapp && (
                    <div>
                      <Phone size={16} /> WhatsApp: {pedidoSelecionado.cliente.whatsapp}
                    </div>
                  )}
                  {pedidoSelecionado.cliente?.email && (
                    <div>
                      <Mail size={16} /> {pedidoSelecionado.cliente.email}
                    </div>
                  )}
                </div>
              </div>

              {/* Endereço de Entrega */}
              {pedidoSelecionado.enderecoEntrega && (
                <div className="pedido-section">
                  <h4>Endereço de Entrega</h4>
                  <div className="pedido-endereco">
                    <MapPin size={16} />
                    <div>
                      {pedidoSelecionado.enderecoEntrega.rua}, {pedidoSelecionado.enderecoEntrega.numero}
                      {pedidoSelecionado.enderecoEntrega.complemento && ` - ${pedidoSelecionado.enderecoEntrega.complemento}`}
                      <br />
                      {pedidoSelecionado.enderecoEntrega.bairro} - {pedidoSelecionado.enderecoEntrega.cidade}/{pedidoSelecionado.enderecoEntrega.estado}
                      <br />
                      CEP: {pedidoSelecionado.enderecoEntrega.cep}
                    </div>
                  </div>
                </div>
              )}

              {/* Itens do Pedido */}
              <div className="pedido-section">
                <h4>Itens do Pedido</h4>
                <div className="pedido-itens">
                  {pedidoSelecionado.itens?.map((item, index) => (
                    <div key={index} className="pedido-item">
                      <div className="item-info">
                        <strong>{item.produto?.nome || item.nome || 'Produto'}</strong>
                        <span>Qtd: {item.quantidade}</span>
                      </div>
                      <div className="item-preco">
                        R$ {item.subtotal?.toFixed(2) || (item.precoUnitario * item.quantidade).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totais */}
              <div className="pedido-section">
                <h4>Resumo Financeiro</h4>
                <div className="pedido-totais">
                  <div className="total-line">
                    <span>Subtotal:</span>
                    <span>R$ {pedidoSelecionado.subtotal?.toFixed(2)}</span>
                  </div>
                  {pedidoSelecionado.tipoEntrega === 'delivery' && (
                    <div className="total-line" style={{ 
                      background: (pedidoSelecionado.taxaEntrega || 0) > 0 ? '#fef3c7' : '#d1fae5',
                      padding: '0.5rem',
                      borderRadius: '0.25rem'
                    }}>
                      <span style={{ fontWeight: '600' }}>Taxa de Entrega:</span>
                      <span style={{ 
                        fontWeight: '700', 
                        color: (pedidoSelecionado.taxaEntrega || 0) > 0 ? '#92400e' : '#059669' 
                      }}>
                        {pedidoSelecionado.taxaEntrega > 0 
                          ? `R$ ${pedidoSelecionado.taxaEntrega.toFixed(2)}` 
                          : 'Grátis'}
                      </span>
                    </div>
                  )}
                  {pedidoSelecionado.desconto > 0 && (
                    <div className="total-line">
                      <span>Desconto:</span>
                      <span>-R$ {pedidoSelecionado.desconto.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="total-line total-final">
                    <span><strong>Total:</strong></span>
                    <span><strong>R$ {pedidoSelecionado.total?.toFixed(2)}</strong></span>
                  </div>
                </div>
              </div>

              {/* Status e Pagamento */}
              <div className="pedido-section">
                <h4>Status e Pagamento</h4>
                <div className="pedido-status-grid">
                  <div>
                    <label>Status do Pedido:</label>
                    <select
                      value={pedidoSelecionado.status}
                      onChange={(e) => {
                        setPedidoSelecionado({ ...pedidoSelecionado, status: e.target.value });
                        handleAtualizarStatus(pedidoSelecionado._id, e.target.value);
                      }}
                      className="status-select"
                    >
                      <option value="Pendente">Pendente</option>
                      <option value="Confirmado">Confirmado</option>
                      <option value="Em preparo">Em preparo</option>
                      <option value="Pronto">Pronto</option>
                      <option value="Saiu para entrega">Saiu para entrega</option>
                      <option value="Entregue">Entregue</option>
                      <option value="Cancelado">Cancelado</option>
                    </select>
                  </div>
                  <div>
                    <label>Status de Pagamento:</label>
                    <select
                      value={pedidoSelecionado.statusPagamento || 'Pendente'}
                      onChange={(e) => {
                        setPedidoSelecionado({ ...pedidoSelecionado, statusPagamento: e.target.value });
                        handleAtualizarPagamento(pedidoSelecionado._id, e.target.value);
                      }}
                      className="status-select"
                    >
                      <option value="Pendente">Pendente</option>
                      <option value="Pago">Pago</option>
                      <option value="Cancelado">Cancelado</option>
                      <option value="Reembolsado">Reembolsado</option>
                    </select>
                  </div>
                </div>
                <div className="pedido-info-grid" style={{ marginTop: '1rem' }}>
                  <div>
                    <CreditCard size={16} /> <strong>Forma de Pagamento:</strong> {pedidoSelecionado.formaPagamento || 'N/A'}
                  </div>
                  <div>
                    <Clock size={16} /> <strong>Tempo Estimado:</strong> {pedidoSelecionado.tempoEstimado || 30} minutos
                  </div>
                  <div>
                    <Truck size={16} /> <strong>Tipo de Entrega:</strong> {pedidoSelecionado.tipoEntrega === 'delivery' ? 'Delivery' : 'Retirada'}
                  </div>
                </div>
              </div>

              {/* Observações */}
              {pedidoSelecionado.observacoes && (
                <div className="pedido-section">
                  <h4>Observações</h4>
                  <p>{pedidoSelecionado.observacoes}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalDetalhes(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pedidos;
